import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useFriends from '@/hooks/contacts/use-friends';
import useUploadFile from '@/hooks/files/use-upload-file';
import useReportReasons from '@/hooks/settings/use-report-reasons';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { RespondedType, ResponseError } from '@/models/response';
import { uploadableScheme } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

interface UseSubmitReportParams {
  sessionId: number;
  type: ConversationType;
  reasonId: number;
}

export default function useSubmitReport({ sessionId, type, reasonId }: UseSubmitReportParams) {
  const { t } = useTranslation();
  const { reasons } = useReportReasons(type);
  const { friends } = useFriends();
  const { upload } = useUploadFile();

  return useMutation<RespondedType, ResponseError, SubmitReportRequest>({
    mutationKey: ['/AddReport'],
    mutationFn: async (params) => {
      const reason = reasons.find((reason) => reason.ID === reasonId)?.Reason ?? '';

      let id = sessionId;

      let uploadedImageUrl: string | undefined;
      if (params.Image) {
        const uploadResult = await upload(params.Image);

        if (uploadResult) {
          uploadedImageUrl = uploadResult.FileUrl;
        }
      }

      if (type === 'session') {
        const friend = Object.values(friends).find((friend) => friend.ChatSessionID === sessionId);
        if (!friend) {
          throw 'BAD_SESSION_ID';
        }
        id = friend.FriendID;
      }

      return fetcher(
        '/AddReport',
        {
          Type: type === 'session' ? '1' : '2',
          [type === 'session' ? 'UserID' : 'GroupID']: id,
          Reason: reason,
          ReasonID: reasonId,
          Image: uploadedImageUrl,
          Describe: params.Describe,
        },
        { validResponseCodes: [0] },
      );
    },
    onSettled: (data) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data ? t('conversation.report.submit-success') : t('conversation.report.submit-error'),
      }),
  });
}

export const submitReportRequestScheme = z.object({
  Describe: z.string().min(5, 'errors.string.min-length|count=5').max(200, 'errors.string.max-length|count=200'),
  Image: uploadableScheme.optional(),
});

export type SubmitReportRequest = z.infer<typeof submitReportRequestScheme>;
